import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface TeamDto {
  id: number;
  organizationId: number;
  name: string;
  color: string;
  dateCreated: string;
  isEnabled?: boolean;
  isDeleted?: boolean;
}

export interface TeamCreateDto {
  organizationId: number;
  name: string;
  color?: string;
}

export interface TeamUpdateDto {
  name: string;
  color?: string;
}

export interface TeamPlayerDto {
  id: number;
  teamId: number;
  userId?: number;
  firstName: string;
  lastName: string;
  order: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
  isEnabled?: boolean;
  isDeleted?: boolean;
  hasCustomSoundArtifact?: boolean;
  hasCustomVideoArtifact?: boolean;
}

export interface TeamPlayerMaintenanceDto {
  id: number;
  teamId: number;
  organizationId: number;
  organizationName: string;
  teamName: string;
  userId?: number;
  firstName: string;
  lastName: string;
  order: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
  hasCustomSoundArtifact?: boolean;
  hasCustomVideoArtifact?: boolean;
  isEnabled: boolean;
  isDeleted: boolean;
}

export interface TeamPlayerCreateDto {
  teamId: number;
  userId?: number;
  firstName: string;
  lastName: string;
  order: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
}

export interface TeamPlayerUpdateDto {
  userId?: number;
  firstName: string;
  lastName: string;
  order: number;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
}

export interface EventTeamAssignmentDto {
  id: number;
  eventId: number;
  teamId: number;
}

export interface EventTeamAssignmentCreateDto {
  eventId: number;
  teamId: number;
}

export interface TeamAssignmentDto {
  id: number;
  teamId: number;
  playerId: number;
  order: number;
  userId?: number;
  firstName: string;
  lastName: string;
  enableCustomSound: boolean;
  enableCustomVideo: boolean;
  hasCustomSoundArtifact?: boolean;
  hasCustomVideoArtifact?: boolean;
}

export interface TeamAssignmentCreateDto {
  teamId: number;
  playerId: number;
  order?: number;
}

export interface TeamAssignmentReorderDto {
  assignmentIds: number[];
}

@Injectable({
  providedIn: 'root'
})
export class TeamService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/teams';
  private readonly teamPlayersApiUrl = '/api/teamplayers';
  private readonly teamAssignmentsApiUrl = '/api/teamassignments';
  private readonly eventTeamsApiUrl = '/api/eventteams';

  /**
   * Get all teams
   */
  getAll(): Observable<TeamDto[]> {
    return this.http.get<TeamDto[]>(this.apiUrl);
  }

  /**
   * Get team by ID
   */
  getById(id: number): Observable<TeamDto> {
    return this.http.get<TeamDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get teams by organization ID
   */
  getByOrganizationId(organizationId: number): Observable<TeamDto[]> {
    return this.http.get<TeamDto[]>(
      `${this.apiUrl}/organization/${organizationId}`
    );
  }

  /**
   * Create new team
   */
  create(data: TeamCreateDto): Observable<TeamDto> {
    return this.http.post<TeamDto>(this.apiUrl, data);
  }

  /**
   * Update team
   */
  update(id: number, data: TeamUpdateDto): Observable<TeamDto> {
    return this.http.put<TeamDto>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete team
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  setEnabled(id: number, isEnabled: boolean): Observable<TeamDto> {
    return this.http.put<TeamDto>(`${this.apiUrl}/${id}/enabled`, { isEnabled });
  }

  restore(id: number): Observable<TeamDto> {
    return this.http.put<TeamDto>(`${this.apiUrl}/${id}/restore`, {});
  }

  hardDelete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/hard`);
  }

  /**
   * Get players by team ID
   */
  getPlayersByTeamId(teamId: number): Observable<TeamPlayerDto[]> {
    return this.http.get<TeamPlayerDto[]>(
      `${this.teamPlayersApiUrl}/team/${teamId}`
    );
  }

  getAllPlayersForMaintenance(): Observable<TeamPlayerMaintenanceDto[]> {
    return this.http.get<TeamPlayerMaintenanceDto[]>(this.teamPlayersApiUrl);
  }

  /**
   * Create player
   */
  createPlayer(data: TeamPlayerCreateDto): Observable<TeamPlayerDto> {
    return this.http.post<TeamPlayerDto>(this.teamPlayersApiUrl, data);
  }

  /**
   * Update player
   */
  updatePlayer(
    id: number,
    data: TeamPlayerUpdateDto
  ): Observable<TeamPlayerDto> {
    return this.http.put<TeamPlayerDto>(`${this.teamPlayersApiUrl}/${id}`, data);
  }

  /**
   * Delete player
   */
  deletePlayer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.teamPlayersApiUrl}/${id}`);
  }

  setPlayerEnabled(id: number, isEnabled: boolean): Observable<TeamPlayerDto> {
    return this.http.put<TeamPlayerDto>(`${this.teamPlayersApiUrl}/${id}/enabled`, { isEnabled });
  }

  restorePlayer(id: number): Observable<TeamPlayerDto> {
    return this.http.put<TeamPlayerDto>(`${this.teamPlayersApiUrl}/${id}/restore`, {});
  }

  hardDeletePlayer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.teamPlayersApiUrl}/${id}/hard`);
  }

  getAssignmentsByTeamId(teamId: number): Observable<TeamAssignmentDto[]> {
    return this.http.get<TeamAssignmentDto[]>(`${this.teamAssignmentsApiUrl}/team/${teamId}`);
  }

  createAssignment(data: TeamAssignmentCreateDto): Observable<TeamAssignmentDto> {
    return this.http.post<TeamAssignmentDto>(this.teamAssignmentsApiUrl, data);
  }

  deleteAssignment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.teamAssignmentsApiUrl}/${id}`);
  }

  reorderAssignments(teamId: number, data: TeamAssignmentReorderDto): Observable<TeamAssignmentDto[]> {
    return this.http.put<TeamAssignmentDto[]>(`${this.teamAssignmentsApiUrl}/team/${teamId}/reorder`, data);
  }

  /**
   * Get event teams by event ID
   */
  getEventTeamsByEventId(eventId: number): Observable<EventTeamAssignmentDto[]> {
    return this.http.get<EventTeamAssignmentDto[]>(`${this.eventTeamsApiUrl}/event/${eventId}`);
  }

  getEventTeamsByTeamId(teamId: number): Observable<EventTeamAssignmentDto[]> {
    return this.http.get<EventTeamAssignmentDto[]>(`${this.eventTeamsApiUrl}/team/${teamId}`);
  }

  /**
   * Assign team to event
   */
  assignTeamToEvent(data: EventTeamAssignmentCreateDto): Observable<EventTeamAssignmentDto> {
    return this.http.post<EventTeamAssignmentDto>(this.eventTeamsApiUrl, data);
  }

  /**
   * Remove event team assignment
   */
  removeEventTeam(id: number): Observable<void> {
    return this.http.delete<void>(`${this.eventTeamsApiUrl}/${id}`);
  }
}
